import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useTrainState, { ITrain } from '@/states/train.state';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

export const useTrainLogic = () => {
  const formControl = useForm<ITrain>({ defaultValues: { name: '', type: '' } });
  const { reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const { trains, paginatedData, getTrains, createTrain, updateTrain, deleteTrain, bulkPublishTrain, isLoading } =
    useTrainState();
  const { currentTenant } = useTenantState();
  const { orgCharts, fetchOrgChartTree } = useOrgChartState();

  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<ITrain | null>(null);
  const [allPublished, setAllPublished] = useState(false);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'name',
    order: 'asc',
  });

  const flattenTree = (nodes: any[]): any[] =>
    nodes.flatMap(node => [node, ...(node.children?.length ? flattenTree(node.children) : [])]);

  useEffect(() => {
    setAllPublished(trains.length > 0 && trains.every(item => item.is_published));
  }, [trains]);

  // Fetch data with server-side pagination and filters
  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      await fetchOrgChartTree(currentTenant.id);
      let isPublishedFilter: boolean | undefined;
      if (selectedStatus === 'Published') {
        isPublishedFilter = true;
      } else if (selectedStatus === 'Unpublished') {
        isPublishedFilter = false;
      }
      await getTrains(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
        type: selectedCategory || undefined,
        name: searchTerm || undefined,
        is_published: isPublishedFilter,
        orderBy: pageConfig.orderBy,
        order: pageConfig.order,
      });
    } catch {
      toast.error('Failed to fetch data');
    }
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.orderBy,
    pageConfig.order,
    selectedRole,
    selectedCategory,
    searchTerm,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Data now comes from server-side pagination
  const displayData = paginatedData || {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
  };

  const allRoles = flattenTree(orgCharts);
  const roleOptions = allRoles.map(node => ({ label: node.name, value: String(node.id) }));

  const handleSave = async (data: ITrain) => {
    if (!currentTenant?.id) return;
    try {
      if (formMode === 'edit' && selectedRow?.id) {
        await updateTrain(currentTenant.id, selectedRow.id, {
          ...data,
          org_chart_node_id: Number(data.org_chart_node_id),
        });
      } else {
        await createTrain(currentTenant.id, { ...data, org_chart_node_id: Number(data.org_chart_node_id) });
      }
      setFormMode(null);
      setSelectedRow(null);
      reset({ name: '', type: '', org_chart_node_id: undefined });
      await fetchData();
    } catch {
      toast.error('Failed to save training');
    }
  };

  const handleDelete = async (row: ITrain) => {
    if (!currentTenant?.id || !row.id) return;
    await deleteTrain(currentTenant.id, row.id);
    await fetchData();
  };

  return {
    formControl,
    formRef,
    formMode,
    setFormMode,
    selectedRow,
    setSelectedRow,
    allPublished,
    isLoading,
    displayData,
    handleSave,
    handleDelete,
    bulkPublishTrain,
    searchTerm,
    setSearchTerm,
    selectedRole,
    setSelectedRole,
    selectedCategory,
    setSelectedCategory,
    selectedStatus,
    setSelectedStatus,
    pageConfig,
    setPageConfig,
    reset,
    currentTenant,
    fetchData,
    roleOptions,
    orgCharts,
  };
};
